import React from 'react';
import PerformanceChart from './PerformanceChart';

/** Short meanings for common 5xx codes shown in the server-errors table. */
const STATUS_MEANINGS = {
  500: 'Internal server error (app/server failure)',
  502: 'Bad gateway (upstream/proxy problem)',
  503: 'Service unavailable (overload or maintenance)',
  504: 'Gateway timeout (upstream too slow)'
};

/** Codes present in the server-errors table head that have a known meaning. */
const statusLegendItems = (table) => (table?.head || [])
  .filter((label) => STATUS_MEANINGS[label])
  .map((code) => ({ code, meaning: STATUS_MEANINGS[code] }));

/** A table cell; `tone` colours it good, bad, steady, watch or elevated, `sub` is a small caption under the text. */
const Cell = ({ cell }) => (
  <td className={cell.tone || undefined}>
    {cell.text}
    {cell.sub ? <small className="sub">{cell.sub}</small> : null}
  </td>
);

const SimpleTable = ({ table }) => (
  <table className="nums">
    <thead>
      <tr>{table.head.map((label) => <th key={label}>{label}</th>)}</tr>
    </thead>
    <tbody>
      {table.rows.map((row, index) => (
        <tr key={row[0].text || index}>{row.map((cell, i) => <Cell cell={cell} key={`${cell.text}-${i}`} />)}</tr>
      ))}
    </tbody>
  </table>
);

/** Compact legend for status codes that appear in the table. */
const StatusCodeLegend = ({ items }) => (items.length
  ? (
    <ul className="perf-code-legend">
      {items.map(({ code, meaning }) => (
        <li key={code}><b>{code}</b> — {meaning}</li>
      ))}
    </ul>
  )
  : null);

const formatAsOf = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  return `${day}, ${time} UTC`;
};

/** Median and 95th percentile by month, from the Datadog months and the curated table. */
const Charts = ({ chart }) => {
  const points = (field) => chart.map((month) => ({
    name: month.label, coverage: month.coverage, value: month[field]
  }));
  return (
    <div className="perf-charts">
      <PerformanceChart title="Median response time by month" points={points('medianMs')} />
      <PerformanceChart title="95th percentile by month" points={points('p95Ms')} />
    </div>
  );
};

/** What the latest synced month says about its rate-limited requests. */
const LatestMonth = ({ latest }) => (latest
  ? latest.lines.map((line) => <p className="note" key={line}>{line}</p>)
  : null);

/** Client performance after the Azure move: headline gains, charts, month table and how to read the states. */
export default function PerformanceView({ performance }) {
  const { details } = performance;
  return (
    <section className="perf">
      <h2>{performance.title}</h2>
      <p className="lead">{performance.lead}</p>
      <div className="perf-kpis">
        {performance.kpis.map((kpi) => (
          <div key={kpi.label}>
            <b className={kpi.tone}>{kpi.value}</b>
            <span>{kpi.label}</span>
          </div>
        ))}
      </div>
      {performance.note ? <p className="note">{performance.note}</p> : null}
      {details && details.asOf ? <p className="note">Data as of {formatAsOf(details.asOf)}.</p> : null}
      {details && details.notes ? details.notes.map((line) => <p className="note" key={line}>{line}</p>) : null}
      {details ? <Charts chart={details.chart} /> : null}
      <SimpleTable table={performance.months} />
      {details ? <LatestMonth latest={details.latest} /> : null}
      {details && details.serverErrors ? (
        <>
          <h2>Server errors by status code</h2>
          <SimpleTable table={details.serverErrors} />
          <StatusCodeLegend items={statusLegendItems(details.serverErrors)} />
          <p className="note">Requests answered with an HTTP 5xx status, counted per month from the synced data.</p>
        </>
      ) : null}
      {performance.states ? (
        <>
          <h2>{performance.statesTitle}</h2>
          <SimpleTable table={performance.states} />
          <p className="note">{performance.statesNote}</p>
        </>
      ) : null}
    </section>
  );
}
